import {
  CreditScoreBlocksDto,
  CreditScoreCompanyDto,
  CreditScoreContributionDto,
  CreditScoreHistoryPointDto,
  CreditScoreMetricValuesDto,
  CreditScoreRatingDto,
  CreditScoreSmoothingOptionDto,
  CreditScoreSmoothingPeriodDto,
} from '@interfaces/credit-score/responses/credit-score.response';

/**
 * Leituras do histórico do score (card 3766), na ordem da página do Felipe
 * (`Vault-Derivative-Pricing`, `static/credit_score.html`, commit 86aa8e3).
 *
 * Contrato v2 (pré-push): **nenhuma conta de score aqui**. Suavização (pesos e janela), nota de cada
 * métrica por exercício, contribuições efetivas e nota por bloco vêm prontas do backend
 * (`scoreHistory[].contributions/blocks/rating`, `smoothingOptions`, `blocks`). Estas funções só
 * escolhem o item certo, ordenam, filtram e subtraem números do backend para exibir variação.
 */

export type CreditScoreMode = 'point' | 'smoothed';
export type CreditScoreTone = 'up' | 'down' | 'neutral';

/** Contribuição exibida (pontual: a do ano; suavizado: a efetiva da opção do backend). */
export type EffectiveContribution = CreditScoreContributionDto;

/** Ponto do histórico com score publicado. */
export interface ScoredHistoryPoint extends CreditScoreHistoryPointDto {
  score: number;
}

export interface ScoreContext {
  score: number | null;
  year: number | null;
  coverage: number;
  /** Exercícios que compõem a nota, do mais recente para trás (pontual: só o ano, peso 1). */
  periods: CreditScoreSmoothingPeriodDto[];
  smoothed: boolean;
  /** O exercício-alvo é o ano corrente (balancete parcial). */
  interim: boolean;
  rating: CreditScoreRatingDto | null;
  contributions: EffectiveContribution[];
  blocks: CreditScoreBlocksDto;
}

export interface HistoricalPosition {
  average: number | null;
  /** 1 = maior score do histórico. */
  rank: number | null;
  total: number;
}

export interface MetricChange extends CreditScoreContributionDto {
  before: number | null;
  after: number | null;
  /** Nota da métrica no ano comparado (backend). */
  scoreBefore: number | null;
  /** Nota da métrica no ano analisado (backend). */
  scoreAfter: number | null;
  scoreDelta: number | null;
  /** Diferença entre as `contribution` dos dois anos (pontos ponderados no score). */
  impact: number | null;
  tone: CreditScoreTone;
}

export interface ScoreDrivers {
  strengths: EffectiveContribution[];
  attention: EffectiveContribution[];
}

export interface MetricTrend {
  text: string;
  tone: CreditScoreTone;
}

type CompanyScoreSource = Pick<
  CreditScoreCompanyDto,
  'contributions' | 'blocks' | 'referenceYear' | 'smoothingOptions'
> &
  Partial<Pick<CreditScoreCompanyDto, 'coverage'>>;

const EMPTY_BLOCKS: CreditScoreBlocksDto = { financial: null, sector: null };
/** Abaixo disso a influência de uma métrica é tratada como neutra (mesmo corte do Felipe). */
const IMPACT_NEUTRAL_BAND = 0.05;
/** Nota da métrica a partir da qual ela é "maior contribuição". */
const STRENGTH_THRESHOLD = 700;
/** Nota da métrica abaixo da qual ela é "ponto de atenção". */
const ATTENTION_THRESHOLD = 500;
const MAX_DRIVERS = 2;

/** Histórico com score publicado e métricas, em ordem crescente de ano. */
export function scoreHistory(
  history: CreditScoreHistoryPointDto[] | null | undefined
): ScoredHistoryPoint[] {
  return (history ?? [])
    .filter((item): item is ScoredHistoryPoint => !!item?.metrics && item.score != null)
    .map(item => ({ ...item, year: Number(item.year), score: Number(item.score) }))
    .sort((a, b) => a.year - b.year);
}

/** Exercício analisado: o ano escolhido, ou o mais recente. */
export function analysisPoint(
  history: ScoredHistoryPoint[],
  year: number | null | undefined
): ScoredHistoryPoint | null {
  return history.find(item => item.year === Number(year)) ?? history.at(-1) ?? null;
}

/** Média do histórico e posição do score entre os exercícios (1º = o maior). */
export function historicalPosition(
  history: ScoredHistoryPoint[],
  score: number | null | undefined
): HistoricalPosition {
  const values = history.map(item => Number(item.score)).filter(Number.isFinite);
  if (!values.length || score == null) {
    return { average: null, rank: null, total: values.length };
  }
  return {
    average: Math.round(values.reduce((sum, value) => sum + value, 0) / values.length),
    rank: 1 + values.filter(value => value > Number(score)).length,
    total: values.length,
  };
}

/** Janelas de suavização que o backend devolveu para o exercício-alvo, em ordem crescente. */
export function smoothingWindows(
  options: CreditScoreSmoothingOptionDto[] | null | undefined,
  year: number | null | undefined
): number[] {
  return (options ?? [])
    .filter(option => option.year === Number(year))
    .map(option => option.window)
    .sort((a, b) => a - b);
}

/** Opção suavizada pronta do backend para (ano, janela); null se não veio. */
export function smoothingOption(
  options: CreditScoreSmoothingOptionDto[] | null | undefined,
  year: number | null | undefined,
  window: number
): CreditScoreSmoothingOptionDto | null {
  return (
    (options ?? []).find(option => option.year === Number(year) && option.window === window) ?? null
  );
}

/** Contribuições e blocos do ano no histórico; sem eles, os da empresa quando o ano é o de referência. */
function pointBreakdown(
  point: ScoredHistoryPoint,
  company: CompanyScoreSource | null
): { contributions: EffectiveContribution[]; blocks: CreditScoreBlocksDto } {
  // No ano de referência a empresa traz a composição completa (o mesmo cálculo do backend); nos
  // demais anos vale a do próprio exercício do histórico.
  if (company && company.referenceYear === point.year) {
    return {
      contributions: company.contributions ?? point.contributions ?? [],
      blocks: company.blocks ?? point.blocks ?? EMPTY_BLOCKS,
    };
  }
  return { contributions: point.contributions ?? [], blocks: point.blocks ?? EMPTY_BLOCKS };
}

/**
 * Nota exibida: no pontual, o próprio exercício do histórico; no suavizado, a opção do backend para
 * (ano analisado, janela). Sem a opção (detalhe ainda não carregado), cai no pontual.
 */
export function scoreContext(
  history: ScoredHistoryPoint[],
  company: CompanyScoreSource | null,
  year: number | null | undefined,
  mode: CreditScoreMode,
  window: number,
  currentYear: number
): ScoreContext {
  const point = analysisPoint(history, year);
  if (!point) {
    // Sem exercício com nota: mostra o exercício e a cobertura que a carteira traz para a empresa
    // (os mesmos da lista), para o card não dizer "0%" onde a lista diz "10%".
    return {
      score: null,
      year: company?.referenceYear ?? null,
      coverage: Number(company?.coverage ?? 0),
      periods: [],
      smoothed: false,
      interim: false,
      rating: null,
      contributions: company?.contributions ?? [],
      blocks: company?.blocks ?? EMPTY_BLOCKS,
    };
  }
  const option =
    mode === 'smoothed' ? smoothingOption(company?.smoothingOptions, point.year, window) : null;
  if (option) {
    return {
      score: option.score,
      year: point.year,
      coverage: Number(point.coverage ?? 0),
      periods: option.periods,
      smoothed: true,
      interim: option.interim,
      rating: option.rating,
      contributions: option.contributions ?? [],
      blocks: option.blocks ?? EMPTY_BLOCKS,
    };
  }
  const breakdown = pointBreakdown(point, company);
  return {
    score: point.score,
    year: point.year,
    coverage: Number(point.coverage ?? 0),
    periods: [{ year: point.year, score: point.score, weight: 1 }],
    smoothed: false,
    interim: point.year === currentYear,
    rating: point.rating ?? null,
    contributions: breakdown.contributions,
    blocks: breakdown.blocks,
  };
}

/** Chave da metodologia (`net_debt_ebitda`) para o campo do DTO de métricas (`netDebtEbitda`). */
export function metricKeyToField(key: string): string {
  return key.replace(/_([a-z0-9])/g, (_, char: string) => char.toUpperCase());
}

function metricValue(
  metrics: CreditScoreMetricValuesDto | null | undefined,
  key: string
): number | null {
  // A metodologia usa a chave do Python (`net_debt_ebitda`); o DTO das métricas vem em camelCase
  // (`netDebtEbitda`). Sem a conversão, toda leitura do histórico voltava vazia.
  const value = metrics?.[metricKeyToField(key)];
  return value == null ? null : Number(value);
}

function toneOf(impact: number | null): CreditScoreTone {
  if (impact == null) return 'neutral';
  if (impact > IMPACT_NEUTRAL_BAND) return 'up';
  if (impact < -IMPACT_NEUTRAL_BAND) return 'down';
  return 'neutral';
}

/**
 * Identidade de um indicador entre exercícios: bloco + chave. O mesmo indicador aparece no bloco geral e
 * no setorial com a mesma `key` (ex.: `net_debt_ebitda`); só pela chave, um ano era comparado com o
 * bloco errado do outro (aparecia "Dívida líquida / EBITDA +100" com o valor igual nos dois anos).
 */
export function contributionId(item: Pick<CreditScoreContributionDto, 'key' | 'block'>): string {
  return `${item.block}:${item.key}`;
}

const TONE_ORDER: Record<CreditScoreTone, number> = { down: 0, up: 1, neutral: 2 };

/**
 * Variação de cada métrica entre dois exercícios. Valores, notas e `contribution` de cada ano vêm do
 * backend (`scoreHistory[].contributions`); a influência é só a diferença entre as duas
 * `contribution`. Ordem: as que pioraram, as que melhoraram, as neutras; dentro de cada grupo,
 * maior impacto.
 */
export function metricChangesBetween(
  previous: Pick<CreditScoreHistoryPointDto, 'contributions'>,
  current: Pick<CreditScoreHistoryPointDto, 'contributions'>
): MetricChange[] {
  const before = new Map((previous.contributions ?? []).map(item => [contributionId(item), item]));
  const after = current.contributions ?? [];
  const keys = new Set(after.map(contributionId));
  const onlyBefore = (previous.contributions ?? []).filter(item => !keys.has(contributionId(item)));
  return [...after, ...onlyBefore]
    .map((metric): MetricChange => {
      const old = before.get(contributionId(metric)) ?? null;
      const now = keys.has(contributionId(metric)) ? metric : null;
      const scoreBefore = old?.score ?? null;
      const scoreAfter = now?.score ?? null;
      const contributionBefore = old?.contribution ?? null;
      const contributionAfter = now?.contribution ?? null;
      const impact =
        contributionBefore != null && contributionAfter != null
          ? contributionAfter - contributionBefore
          : null;
      return {
        ...metric,
        before: old?.value ?? null,
        after: now?.value ?? null,
        scoreBefore,
        scoreAfter,
        scoreDelta: scoreBefore != null && scoreAfter != null ? scoreAfter - scoreBefore : null,
        impact,
        tone: toneOf(impact),
      };
    })
    .sort((a, b) =>
      a.tone === b.tone
        ? Math.abs(b.impact ?? 0) - Math.abs(a.impact ?? 0)
        : TONE_ORDER[a.tone] - TONE_ORDER[b.tone]
    );
}

/** Métricas que pioraram entre dois exercícios. */
export function deteriorationsBetween(
  previous: Pick<CreditScoreHistoryPointDto, 'contributions'>,
  current: Pick<CreditScoreHistoryPointDto, 'contributions'>
): MetricChange[] {
  return metricChangesBetween(previous, current).filter(item => item.tone === 'down');
}

/** Até duas maiores contribuições (nota ≥ 700) e dois pontos de atenção (nota < 500), só gerais. */
export function scoreDrivers(contributions: EffectiveContribution[]): ScoreDrivers {
  const available = contributions.filter(item => item.block === 'financial' && item.score != null);
  return {
    strengths: available
      .filter(item => Number(item.score) >= STRENGTH_THRESHOLD)
      .sort((a, b) => Number(b.score) - Number(a.score))
      .slice(0, MAX_DRIVERS),
    attention: available
      .filter(item => Number(item.score) < ATTENTION_THRESHOLD)
      .sort((a, b) => Number(a.score) - Number(b.score))
      .slice(0, MAX_DRIVERS),
  };
}

/** Variação do valor da métrica contra o exercício anterior com dado (cartão de indicador). */
export function metricTrend(
  history: ScoredHistoryPoint[],
  metric: Pick<EffectiveContribution, 'key' | 'value' | 'source' | 'direction'>,
  year: number | null,
  smoothed: boolean,
  formatDelta: (value: number) => string
): MetricTrend | null {
  if (smoothed) return { text: 'Média ponderada da janela selecionada', tone: 'neutral' };
  const prior = history
    .filter(
      item => year != null && item.year < year && metricValue(item.metrics, metric.key) != null
    )
    .at(-1);
  if (metric.value == null || !prior) {
    return metric.source === 'estimated'
      ? { text: 'FCO estimado pelo método indireto', tone: 'neutral' }
      : null;
  }
  const delta = Number(metric.value) - Number(metricValue(prior.metrics, metric.key));
  const estimatedNote = metric.source === 'estimated' ? ' · FCO estimado' : '';
  // Diferença que some no arredondamento da tela não é variação ("↓ -0,00" confundia).
  if (formatDelta(Math.abs(delta)) === formatDelta(0)) {
    return { text: `sem variação vs ${prior.year}${estimatedNote}`, tone: 'neutral' };
  }
  const arrow = delta > 0 ? '↑ +' : delta < 0 ? '↓ ' : '';
  // A seta mostra o sentido do número; o tom diz se melhorou (`up`, verde) ou piorou (`down`,
  // vermelho). Em "menor é melhor" (alavancagem, instabilidade), cair é melhorar.
  const improved = metric.direction === 'lower' ? delta < 0 : delta > 0;
  return {
    text: `${arrow}${formatDelta(delta)} vs ${prior.year}${estimatedNote}`,
    tone: delta === 0 ? 'neutral' : improved ? 'up' : 'down',
  };
}

/** Valor e nota do mesmo indicador (bloco geral) no ano comparado, e a variação do valor. */
export interface MetricComparison {
  value: number | null;
  score: number | null;
  trend: MetricTrend | null;
}

/**
 * Indicador do ano analisado contra o mesmo indicador do ano comparado (o "Comparar com" da evolução):
 * valor e nota que o backend mandou para aquele ano e a variação do valor, colorida pela direção.
 */
export function metricComparison(
  comparison: Pick<ScoredHistoryPoint, 'year' | 'contributions'>,
  metric: Pick<EffectiveContribution, 'key' | 'block' | 'value' | 'source' | 'direction'>,
  formatDelta: (value: number) => string
): MetricComparison {
  const previous =
    (comparison.contributions ?? []).find(
      item => contributionId(item) === contributionId(metric)
    ) ?? null;
  const before = previous?.value == null ? null : Number(previous.value);
  const score = previous?.score == null ? null : Number(previous.score);
  if (before == null || metric.value == null) return { value: before, score, trend: null };
  const delta = Number(metric.value) - before;
  if (formatDelta(Math.abs(delta)) === formatDelta(0)) {
    return { value: before, score, trend: { text: 'sem variação', tone: 'neutral' } };
  }
  const improved = metric.direction === 'lower' ? delta < 0 : delta > 0;
  return {
    value: before,
    score,
    trend: {
      text: `${delta > 0 ? '↑ +' : '↓ '}${formatDelta(delta)}`,
      tone: improved ? 'up' : 'down',
    },
  };
}

/** Faixa do eixo Y do gráfico de evolução: folga e múltiplos de 25, mínimo de 100 pontos. */
export function historyChartRange(scores: number[]): { low: number; high: number; step: number } {
  if (!scores.length) return { low: 0, high: 1000, step: 200 };
  const minimum = Math.min(...scores);
  const maximum = Math.max(...scores);
  const padding = Math.max(35, (maximum - minimum) * 0.14);
  // Passo redondo das marcas do eixo: 50 em faixa curta, 100 acima de 300 pontos.
  const step = maximum - minimum + 2 * padding > 300 ? 100 : 50;
  let low = Math.max(0, Math.floor((minimum - padding) / step) * step);
  let high = Math.min(1000, Math.ceil((maximum + padding) / step) * step);
  if (high - low < 100) {
    low = Math.max(0, low - 50);
    high = Math.min(1000, high + 50);
  }
  return { low, high, step };
}

/** Um indicador no painel da evolução: o geral e o setorial de mesma chave numa linha só. */
export interface YearChangeItem {
  key: string;
  /** Nome do indicador geral (o setorial tem outro nome no backend, mas é o mesmo número). */
  label: string;
  unit: string;
  before: number | null;
  after: number | null;
  /** Soma das diferenças de `contribution` dos blocos (pontos ponderados na média). */
  impact: number;
  tone: CreditScoreTone;
  /** Diferença de cada bloco, quando o indicador aparece nos dois. */
  parts: { block: string; impact: number }[];
  estimated: boolean;
}

export interface YearChangeSummary {
  /** Indicadores que mexeram na nota, do maior impacto para o menor. */
  top: YearChangeItem[];
  /** Quantos puxaram a nota para baixo e para cima. */
  down: number;
  up: number;
  /** Indicadores que tinham dado no ano comparado e ficaram sem dado: saem da média e baixam a cobertura. */
  missing: YearChangeItem[];
}

/**
 * Resumo do que mudou entre dois exercícios para o painel da evolução. O mesmo indicador aparece no bloco
 * geral e no setorial (mesma `key`, nomes diferentes, mesmo valor): aqui eles viram uma linha só, com a
 * diferença de cada bloco, para não parecerem dois indicadores. Só agrupa, soma as diferenças do backend,
 * ordena e conta.
 */
export function yearChangeSummary(
  previous: Pick<CreditScoreHistoryPointDto, 'contributions'>,
  current: Pick<CreditScoreHistoryPointDto, 'contributions'>,
  limit = 4
): YearChangeSummary {
  const groups = new Map<string, MetricChange[]>();
  for (const change of metricChangesBetween(previous, current)) {
    groups.set(change.key, [...(groups.get(change.key) ?? []), change]);
  }
  const items = [...groups.values()].map((group): YearChangeItem => {
    const main = group.find(item => item.block === 'financial') ?? group[0];
    const parts = group
      .filter(item => item.impact != null)
      .map(item => ({ block: item.block, impact: item.impact as number }));
    const impact = parts.reduce((sum, part) => sum + part.impact, 0);
    return {
      key: main.key,
      label: main.label,
      unit: main.unit,
      before: main.before,
      after: main.after,
      impact,
      tone: parts.length ? toneOf(impact) : 'neutral',
      parts: parts.length > 1 ? parts : [],
      estimated: group.some(item => item.source === 'estimated'),
    };
  });
  const moved = items
    .filter(item => item.tone !== 'neutral')
    .sort((a, b) => Math.abs(b.impact) - Math.abs(a.impact));
  return {
    top: moved.slice(0, limit),
    down: moved.filter(item => item.tone === 'down').length,
    up: moved.filter(item => item.tone === 'up').length,
    missing: items.filter(item => item.before != null && item.after == null),
  };
}
